/**
 * photoMove — pure helpers for the office "Wrong unit" exit (callable fieldService_galleryMove, #6204).
 *
 * WHO SEES THE BUTTON is a convenience, NOT access control: the server decides (moveService refuses anyone who is not
 * office, from the caller's verified token and users doc). `canOfferPhotoMove` mirrors the two parts of that rule this
 * app can see (users.role and users.roleTags; the token-claim part is server-only), so a technician is not shown a
 * button the server would refuse.
 */
import type { GeneratorPhoto } from '../../types/fieldService/generatorPhotos';

/** Same shape the server accepts for a Generator ID (moveService GENERATOR_ID_RE). */
const GENERATOR_ID_RE = /^[A-Za-z0-9._-]{1,100}$/;

const OFFICE_ROLES: ReadonlySet<string> = new Set(['Owner', 'Admin', 'Manager']);
const OFFICE_ROLE_TAGS: ReadonlySet<string> = new Set(['office_staff', 'management']);

export function canOfferPhotoMove(user: { role?: string | null; roleTags?: readonly string[] | null } | null | undefined): boolean {
  if (!user) return false;
  if (typeof user.role === 'string' && OFFICE_ROLES.has(user.role)) return true;
  return Array.isArray(user.roleTags) && user.roleTags.some((t) => OFFICE_ROLE_TAGS.has(t));
}

/** A removed / duplicate photo cannot be moved (the server refuses it): no button rather than a refusal. */
export function canMovePhoto(photo: Pick<GeneratorPhoto, 'state' | 'generatorId'>): boolean {
  return photo.state !== 'hidden' && typeof photo.generatorId === 'string' && photo.generatorId !== '';
}

export type MoveTargetCheck = { ok: true; toGeneratorId: string } | { ok: false; reason: string };

/** What the office typed, checked before anything is sent. The server resolves the ID to a unit; this only catches typos early. */
export function checkMoveTarget(typed: string, fromGeneratorId: string): MoveTargetCheck {
  const toGeneratorId = typed.trim();
  if (toGeneratorId === '') return { ok: false, reason: 'Type the Generator ID of the unit this photo belongs to.' };
  if (!GENERATOR_ID_RE.test(toGeneratorId)) return { ok: false, reason: 'That is not a Generator ID. Use letters, numbers, dots and dashes only.' };
  if (toGeneratorId === fromGeneratorId) return { ok: false, reason: 'The photo is already on that unit.' };
  return { ok: true, toGeneratorId };
}
